import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, forkJoin, map, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CategoryDTO,
  SupplierDTO,
  SupplierLocationDTO,
  UnitOfMeasureDTO,
  EnhancedItemDTO,
  InventoryTransactionDTO,
} from '../../components/UI/inventory-dashboard/inventory-dashboard.component';

export interface ItemTypeDTO {
  itemTypeID: number;
  typeName: string;
}

export interface LoadedItems {
  items: EnhancedItemDTO[];
  transactions: InventoryTransactionDTO[];
}

/**
 * Centralizes the inventory-related HttpClient calls that used to live
 * inline in InventoryDashboardComponent. Methods return Observables; the
 * component subscribes and assigns results to its own fields, matching the
 * component's existing style (loadCategories/loadSuppliers already returned
 * Observables before this extraction).
 */
@Injectable({ providedIn: 'root' })
export class InventoryDataService {
  constructor(private http: HttpClient) {}

  /** Bearer-auth headers, built once from the stored auth token. Was duplicated across nearly every method in the component. */
  private authHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  /**
   * Loads Items + InventoryTransactions together, as the component's
   * loadItems() did (forkJoin of Items/GetAll + InventoryTransactions/GetAll).
   * Stock-level derivation from the transactions stays in the component,
   * since it also folds in category names and BOM-based totals there.
   */
  loadItems(): Observable<LoadedItems> {
    return forkJoin({
      items: this.http.get<EnhancedItemDTO[]>(`${environment.apiUrl}/api/Items/GetAll`, { headers: this.authHeaders() }),
      transactions: this.http.get<InventoryTransactionDTO[]>(`${environment.apiUrl}/api/InventoryTransactions/GetAll`, { headers: this.authHeaders() }),
    });
  }

  /**
   * Loads only items of a single itemTypeID (e.g. 1 = Raw Material) via the
   * backend's `GET /api/Items/ByType/{itemTypeId}` endpoint, plus the same
   * InventoryTransactions/GetAll join loadItems() does — matching its
   * LoadedItems shape so callers don't need to branch on which was used.
   */
  loadItemsByType(itemTypeId: number): Observable<LoadedItems> {
    return forkJoin({
      items: this.http.get<EnhancedItemDTO[]>(`${environment.apiUrl}/api/Items/ByType/${itemTypeId}`, { headers: this.authHeaders() }),
      transactions: this.http.get<InventoryTransactionDTO[]>(`${environment.apiUrl}/api/InventoryTransactions/GetAll`, { headers: this.authHeaders() }),
    });
  }

  /**
   * Loads items across several itemTypeIDs (e.g. [2, 4] = Finished Good +
   * Service) by calling the single-type ByType endpoint once per type and
   * merging the results client-side, since the endpoint only accepts one
   * itemTypeId at a time. Transactions are fetched once and shared.
   */
  loadItemsByTypes(itemTypeIds: number[]): Observable<LoadedItems> {
    const itemRequests = itemTypeIds.map(itemTypeId =>
      this.http.get<EnhancedItemDTO[]>(`${environment.apiUrl}/api/Items/ByType/${itemTypeId}`, { headers: this.authHeaders() })
    );
    return forkJoin({
      items: forkJoin(itemRequests).pipe(map(itemArrays => itemArrays.flat())),
      transactions: this.http.get<InventoryTransactionDTO[]>(`${environment.apiUrl}/api/InventoryTransactions/GetAll`, { headers: this.authHeaders() }),
    });
  }

  loadCategories(): Observable<CategoryDTO[]> {
    return this.http.get<CategoryDTO[]>(`${environment.apiUrl}/api/Category/GetAll`, { headers: this.authHeaders() });
  }

  loadSuppliers(): Observable<SupplierDTO[]> {
    return this.http.get<SupplierDTO[]>(`${environment.apiUrl}/api/Suppliers/GetAll`, { headers: this.authHeaders() });
  }

  loadUnitsOfMeasure(): Observable<UnitOfMeasureDTO[]> {
    return this.http.get<UnitOfMeasureDTO[]>(`${environment.apiUrl}/api/UnitOfMeasure/GetAll`, { headers: this.authHeaders() });
  }

  loadItemTypes(): Observable<ItemTypeDTO[]> {
    return this.http.get<ItemTypeDTO[]>(`${environment.apiUrl}/api/ItemTypes/GetAll`, { headers: this.authHeaders() });
  }

  /** Creates a supplier, then its location, returning both. Mirrors the two-step POST previously inline in createSupplier(). */
  createSupplier(supplier: SupplierDTO, location: Partial<SupplierLocationDTO>): Observable<{ supplier: SupplierDTO; location: SupplierLocationDTO }> {
    const headers = this.authHeaders();
    return this.http.post<SupplierDTO>(`${environment.apiUrl}/api/Suppliers/Add`, supplier, { headers }).pipe(
      switchMap((supplierResponse) => {
        const newLocation = { ...location, locationID: 0, supplierID: supplierResponse.supplierID };
        return this.http.post<SupplierLocationDTO>(`${environment.apiUrl}/api/SupplierLocations/Add`, newLocation, { headers }).pipe(
          map((locationResponse) => ({ supplier: supplierResponse, location: locationResponse }))
        );
      })
    );
  }

  createCategory(category: CategoryDTO): Observable<CategoryDTO> {
    return this.http.post<CategoryDTO>(`${environment.apiUrl}/api/Category/Add`, category, { headers: this.authHeaders() });
  }

  /** Deterministic SKU generator (DJB2-based) — unchanged from the component's original generateSKU(). */
  generateSKU(productName: string): string {
    if (!productName) return '';

    const cleanName = productName
      .trim()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .toUpperCase();

    const words = cleanName.split(/\s+/).filter(Boolean);

    let letters: string;
    if (words.length >= 2) {
      letters = (words[0][0] || 'X') + (words[1].substring(0, 2) || 'XX');
      letters = letters.padEnd(3, 'X');
    } else if (words.length === 1) {
      letters = words[0].substring(0, 3).padEnd(3, 'X');
    } else {
      letters = 'XXX';
    }

    const hash = (str: string): number => {
      let h = 5381;
      for (let i = 0; i < str.length; i++) {
        h = ((h << 5) + h) ^ str.charCodeAt(i);
      }
      return Math.abs(h);
    };

    const alphaOnly = cleanName.replace(/[^A-Z]/g, '');
    const mid = hash(alphaOnly || cleanName) % 1000;
    const last = hash(cleanName + '|SKU') % 1000;

    return `${letters}-${mid.toString().padStart(3, '0')}-${last.toString().padStart(3, '0')}`;
  }
}
